import { AnalyticsBrowser } from '@segment/analytics-next';
import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust';
import { Plugin as SegmentPlugin } from '@segment/analytics-next';
import { parseCookies, setCookie } from 'nookies';
import { getUrlParams } from './utils/get-url-params';
import { getOptimizelyEdgeExperiments } from './utils/get-optimizely-edge-experiments';
import { getClientIp } from './client-ip';
import { getActiveGroups } from './consent-reload';
import { identityLinkPlugin } from './identity-link';

const NON_ESSENTIAL_CATEGORIES = ['C0002', 'C0003', 'C0004', 'C0005'];

export const analytics = new AnalyticsBrowser();

// This allows DigiOh's Segment Track & Traits integration to use our analytics instance
if (typeof window !== 'undefined') {
  window.analytics = analytics;
}

let analyticsLoaded = false;

export const loadAnalytics = () => {
  if (analyticsLoaded || typeof window === 'undefined') return;
  if (!process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY) return;

  analyticsLoaded = true;

  withOneTrust(analytics, {
    // OneTrust's domain is configured as opt-out (CCPA default-allow). The
    // wrapper's opt-out path loads every destination and only blocks events
    // *after* their SDKs have bootstrapped, which leaks Hotjar/ttq/gtag/etc.
    // Switch to opt-in once the user has actively denied any non-essential
    // category so the wrapper filters denied destinations at load time.
    consentModel: () => {
      // First-time visitors haven't produced an OptanonConsent cookie yet,
      // and OnetrustActiveGroups can be empty before OneTrust finishes
      // populating it. Without this guard, an empty groups array would be
      // misread as "every non-essential category denied" and flip the
      // wrapper to opt-in, which then waits for the banner to be closed
      // before loading Segment — suppressing analytics for users who just
      // ignore the banner. Mirror the cookie-presence signal already used
      // in pages/_document.tsx for Google Consent Mode wait_for_update.
      const hasConsentCookie =
        typeof document !== 'undefined' &&
        document.cookie.indexOf('OptanonConsent=') !== -1;
      if (!hasConsentCookie) return 'opt-out';

      const groups = getActiveGroups();
      const hasAnyDenial = NON_ESSENTIAL_CATEGORIES.some(
        (c) => !groups.includes(c),
      );
      return hasAnyDenial ? 'opt-in' : 'opt-out';
    },
  })
    .load(
      {
        writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
        plugins: [
          ipEnrichmentPlugin,
          campaignPlugin,
          optimizelyEdgePlugin,
          identityLinkPlugin,
        ],
      },
      { initialPageview: false },
    )
    .catch((error) => {
      console.error('Failed to load Segment analytics:', error);
      analyticsLoaded = false;
    });
};

// IP enrichment plugin - adds client IP to context for all event types
export const ipEnrichmentPlugin: SegmentPlugin = {
  name: 'IP Enrichment',
  version: '0.1.0',
  type: 'enrichment',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: async (ctx) => {
    const ip = await getClientIp();
    if (ip) ctx.updateEvent('context.ip', ip);
    return ctx;
  },
  page: async (ctx) => {
    const ip = await getClientIp();
    if (ip) ctx.updateEvent('context.ip', ip);
    return ctx;
  },
  identify: async (ctx) => {
    const ip = await getClientIp();
    if (ip) ctx.updateEvent('context.ip', ip);
    return ctx;
  },
};

// Campaign tracking utilities
const decodeParameter = (utmParameter: string): string =>
  decodeURIComponent(utmParameter.replace(/\+/g, ' '));

const generatedCampaignObject = () => ({
  name: decodeParameter(localStorage.getItem('utm_campaign_last') || ''),
  source: decodeParameter(localStorage.getItem('utm_source_last') || ''),
  medium: decodeParameter(localStorage.getItem('utm_medium_last') || ''),
  term: decodeParameter(localStorage.getItem('utm_term_last') || ''),
  content: decodeParameter(localStorage.getItem('utm_content_last') || ''),
  id: decodeParameter(localStorage.getItem('utm_id_last') || ''),
});

// Facebook Click ID (fbc) utilities
// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
const getSubdomainIndex = () => {
  const hostname = window.location.hostname.split('.');
  if (hostname.length === 1) return 0; // e.g., localhost
  if (hostname.length === 2) return 1; // e.g., example.com
  return 2; // e.g., www.example.com
};

const formatFbc = (fbclid: string) => {
  const version = 'fb';
  const subdomainIndex = getSubdomainIndex();
  const creationTime = Date.now();
  return `${version}.${subdomainIndex}.${creationTime}.${fbclid}`;
};

// Reads the _fbc cookie (set by the FB pixel). If the pixel hasn't run yet
// but the URL has an fbclid, we write the cookie ourselves in the same format
// the pixel uses so the next read finds it.
export const getOrCreateFbc = () => {
  const cookieFbc = parseCookies()._fbc;
  if (cookieFbc) return cookieFbc;

  const fbclid = getUrlParams('fbclid');
  if (fbclid) {
    const fbc = formatFbc(fbclid);
    setCookie(null, '_fbc', fbc, {
      maxAge: 60 * 60 * 24 * 90,
      path: '/',
    });
    return fbc;
  }

  return null;
};

const formatFbp = () => {
  const version = 'fb';
  const subdomainIndex = getSubdomainIndex();
  const creationTime = Date.now();
  const randomNumber = Math.floor(Math.random() * 1e10);
  return `${version}.${subdomainIndex}.${creationTime}.${randomNumber}`;
};

// Reads the _fbp cookie (set by the FB pixel). If the pixel hasn't run yet,
// we generate a value in the format Meta documents and write the cookie
// ourselves so events fired before the pixel loads still carry an fbp.
export const getOrCreateFbp = () => {
  const cookieFbp = parseCookies()._fbp;
  if (cookieFbp) return cookieFbp;

  const fbp = formatFbp();
  setCookie(null, '_fbp', fbp, {
    maxAge: 60 * 60 * 24 * 90,
    path: '/',
  });
  return fbp;
};

// Caller-set userData fields win over plugin values.
const enrichUserDataWithMetaIds = (ctx: { event: any; updateEvent: any }) => {
  const fbc = getOrCreateFbc();
  const fbp = getOrCreateFbp();
  if (!fbp && !fbc) return;
  const existingUserData = ctx.event.properties?.userData || {};
  ctx.updateEvent('properties.userData', {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    ...existingUserData,
  });
};

// Campaign enrichment plugin - adds UTM and Facebook params to events
export const campaignPlugin: SegmentPlugin = {
  name: 'Campaign Integration',
  version: '0.1.0',
  type: 'enrichment',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  page: (ctx) => {
    const fbc = getOrCreateFbc();
    const fbp = getOrCreateFbp();
    ctx.updateEvent('context.campaign', generatedCampaignObject());
    ctx.updateEvent('properties.eventId', ctx.event.messageId);
    if (fbc) ctx.updateEvent('properties.fbc', fbc);
    if (fbp) ctx.updateEvent('properties.fbp', fbp);
    enrichUserDataWithMetaIds(ctx);
    return ctx;
  },
  track: (ctx) => {
    const fbc = getOrCreateFbc();
    const fbp = getOrCreateFbp();
    ctx.updateEvent('context.campaign', generatedCampaignObject());
    if (!ctx.event.properties?.eventId) {
      ctx.updateEvent('properties.eventId', ctx.event.messageId);
    }
    if (fbc) ctx.updateEvent('properties.fbc', fbc);
    if (fbp) ctx.updateEvent('properties.fbp', fbp);
    enrichUserDataWithMetaIds(ctx);
    return ctx;
  },
  identify: (ctx) => {
    const fbc = getOrCreateFbc();
    const fbp = getOrCreateFbp();
    ctx.updateEvent('context.campaign', generatedCampaignObject());
    if (fbc) ctx.updateEvent('traits.fbc', fbc);
    if (fbp) ctx.updateEvent('traits.fbp', fbp);
    return ctx;
  },
};

// Optimizely Edge experiments plugin
const optimizelyEdgeExperiments = getOptimizelyEdgeExperiments();

export const optimizelyEdgePlugin: SegmentPlugin = {
  name: 'Optimizely Edge Integration',
  version: '0.1.0',
  type: 'enrichment',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  identify: (ctx) => {
    if (optimizelyEdgeExperiments) {
      ctx.updateEvent(
        'traits.optimizelyEdgeExperiments',
        optimizelyEdgeExperiments,
      );
    }
    return ctx;
  },
};

export const getSegmentAnonymousId = async (): Promise<string | null> => {
  try {
    const user = await analytics.user();
    return user.anonymousId() || null;
  } catch {
    return null;
  }
};

/**
 * Synchronously reads the Segment anonymous ID from the `ajs_anonymous_id`
 * cookie. Returns null when the cookie hasn't been written yet (e.g. Segment
 * hasn't loaded or consent hasn't been granted). Use this as a fast-path
 * fallback so cart-attribute writes don't depend on Segment being fully loaded.
 */
export const getSegmentAnonymousIdSync = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)ajs_anonymous_id=([^;]*)/);
  if (!match) return null;
  try {
    // The cookie value is URI-encoded and may be wrapped in quotes
    const raw = decodeURIComponent(match[1]);
    return raw.replace(/^"|"$/g, '') || null;
  } catch {
    return null;
  }
};

/**
 * Returns the Segment anonymous ID, falling back to the synchronous cookie
 * reader if the async call doesn't resolve within `timeoutMs`.
 * This prevents `analytics.user()` from hanging indefinitely when Segment
 * hasn't loaded (e.g. consent not yet granted, ad-blocker, missing write key).
 */
export const getSegmentAnonymousIdWithTimeout = (
  timeoutMs = 2000,
): Promise<string | null> =>
  Promise.race([
    getSegmentAnonymousId(),
    new Promise<string | null>((resolve) =>
      setTimeout(() => resolve(getSegmentAnonymousIdSync()), timeoutMs),
    ),
  ]);

export default analytics;
